import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OrgAPI from 'api/orbitV1/OrgAPI'
import UserAPI from 'api/orbitV1/UserAPI'
import { FormikProps } from 'formik'
import { formatAddress } from 'helpers/formatAddress'
import OrbitSession from 'hooks/useCookies'
import {
  IAddressDetails,
  IOrgDetails,
  IOrgFormDetails
} from 'types/api/v1/org.type'

function ReviewOrder({
  formik,
  orgId,
  setCompanyFormStatus
}: {
  formik: FormikProps<IOrgFormDetails>
  orgId: string
  setCompanyFormStatus: (value: boolean) => void
}) {
  const userId = OrbitSession.get('_id')
  const [isBillingAddrSame, setIsBillingAddrSame] = useState(false)

  const {
    status,
    data: orgData,
    error
  } = useQuery(['getOrganisationAddress', 'organisation'], async () => {
    const { data } = await OrgAPI.getOrgDetails(orgId)
    return data
  })

  const {
    status: userStatus,
    data: userData,
    error: userError
  } = useQuery(['getUserData', 'user'], async () => {
    const { data } = await UserAPI.getUserById(userId)
    return data
  })

  if (error) {
    toast.error('Error loading organisation data')
  }

  if (userError) {
    toast.error('Error loading user data')
  }

  const setFormikAddress = (
    address: string,
    formikDetails: IAddressDetails
  ): void => {
    formik.setFieldValue(`${address}.blockNo`, formikDetails.blockNo)
    formik.setFieldValue(`${address}.streetName`, formikDetails.streetName)
    formik.setFieldValue(`${address}.buildingName`, formikDetails.buildingName)
    formik.setFieldValue(`${address}.postalCode`, formikDetails.postalCode)
  }

  /*preload company details*/
  useEffect(() => {
    if (status === 'success' && userStatus === 'success') {
      const orgDataAll: IOrgDetails = orgData[0]
      const orgDataDeliveryAddress = orgData[0].deliveryAddress
      const billingDataDeliveryAddress = orgData[0].billingAddress

      setFormikAddress('deliveryAddress', orgDataDeliveryAddress)
      setFormikAddress('billingAddress', billingDataDeliveryAddress)
      formik.setFieldValue('uen', orgDataAll.uen)

      //to set initial state whether billing address same as delivery address based on db
      setIsBillingAddrSame(
        formatAddress(billingDataDeliveryAddress) ===
          formatAddress(orgDataDeliveryAddress)
      )

      formik.setFieldValue('companyName', orgDataAll.name)
      formik.setFieldValue('email', userData[0].email)
      setCompanyFormStatus(formik.isValid)
    }
  }, [status])

  useEffect(() => {
    setCompanyFormStatus(formik.isValid)
  }, [formik.isValid])

  const handleBillingCheckbox = (event) => {
    setIsBillingAddrSame(event.target.checked)
    // if billing address same as delivery address, set same address
    if (event.target.checked) {
      setFormikAddress('billingAddress', orgData[0].deliveryAddress)
    }
  }

  const handleAddressChange = (e) => {
    if (isBillingAddrSame) {
      formik.setFieldValue(
        `billingAddress.${e.target.name.split('.')[1]}`,
        e.target.value
      )
    }
  }

  if (status !== 'success' || userStatus !== 'success') {
    return <CircularProgress />
  }
  return (
    <div data-cy='reviewOrder'>
      <Box>
        <Typography variant='h6' sx={{ color: 'primary.dark' }}>
          Please Key In Your Details:{' '}
        </Typography>
        <Typography variant='subtitle1'>
          These details will be attached to Orbit portal account. Your
          information is always secure in our hands.
        </Typography>
      </Box>
      <Box>
        <FormLabel className='formSectionHeader'>Company Information</FormLabel>
      </Box>
      <Stack direction='row'>
        <Stack sx={{ width: '50%', marginTop: '8px' }}>
          <FormLabel className='formSmallLabel'>Company Name</FormLabel>
          <TextField
            data-cy='companyName'
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            sx={{ width: '90%', marginTop: '8px' }}
            variant='standard'
            name='companyName'
            value={formik.values.companyName}
            error={
              formik.touched.companyName && Boolean(formik.errors.companyName)
            }
            helperText={formik.touched.companyName && formik.errors.companyName}
          />
        </Stack>
        <Stack
          sx={{
            width: '50%',
            marginTop: '8px'
          }}
        >
          <FormLabel className='formSmallLabel'>
            Unique Entity Number (UEN)
          </FormLabel>
          <TextField
            data-cy='uen'
            value={formik.values.uen}
            variant='standard'
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            sx={{ width: '90%', marginTop: '8px' }}
            name='uen'
            error={
              formik.values.uen === '' ||
              (formik.touched.uen && Boolean(formik.errors.uen))
            }
            helperText={
              (formik.values.uen === '' && formik.errors.uen) ||
              (formik.touched.uen && formik.errors.uen)
            }
            fullWidth
          />
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: '50%'
        }}
      >
        <FormLabel className='formSmallLabel'>Company Email</FormLabel>
        <TextField
          data-cy='companyEmail'
          name='email'
          variant='standard'
          size='small'
          sx={{ width: '90%', marginTop: '8px' }}
          onChange={(e) => {
            formik.handleBlur(e)
            formik.handleChange(e)
          }}
          value={formik.values.email}
          error={
            formik.values.email === '' ||
            (formik.touched.email && Boolean(formik.errors.email))
          }
          helperText={formik.touched.email && formik.errors.email}
        />
      </Stack>
      <Box sx={{ marginTop: '16px' }}>
        <FormLabel className='formSectionHeader'>Delivery Address</FormLabel>
      </Box>
      <Stack direction='row'>
        <Stack
          sx={{
            width: '50%',
            marginTop: '8px'
          }}
        >
          <FormLabel className='formSmallLabel'>Address</FormLabel>
          <TextField
            data-cy='deliveryAddress'
            name='deliveryAddress.streetName'
            variant='standard'
            fullWidth
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
              handleAddressChange(e)
            }}
            sx={{ width: '90%' }}
            value={formik.values.deliveryAddress.streetName}
            error={
              formik.values.deliveryAddress.streetName === '' ||
              (formik.touched.deliveryAddress?.streetName &&
                Boolean(formik.errors.deliveryAddress?.streetName))
            }
            helperText={
              (formik.values.deliveryAddress.streetName === '' &&
                formik.errors.deliveryAddress?.streetName) ||
              (formik.touched.deliveryAddress?.streetName &&
                formik.errors.deliveryAddress?.streetName)
            }
          />
        </Stack>
        <Stack
          sx={{
            width: '50%',
            marginTop: '8px'
          }}
        >
          <FormLabel className='formSmallLabel'>Unit No.</FormLabel>
          <TextField
            data-cy='officeUnitNo'
            sx={{
              width: '90%'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>#</InputAdornment>
              )
            }}
            value={formik.values.deliveryAddress.unitNo}
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
              handleAddressChange(e)
            }}
            error={
              formik.values.deliveryAddress.unitNo === '' ||
              (formik.touched.deliveryAddress?.unitNo &&
                Boolean(formik.errors.deliveryAddress?.unitNo))
            }
            helperText={
              (formik.values.deliveryAddress.unitNo === '' &&
                formik.errors.deliveryAddress?.unitNo) ||
              (formik.touched.deliveryAddress?.unitNo &&
                formik.errors.deliveryAddress?.unitNo)
            }
            id='deliveryAddress.unitNo'
            name='deliveryAddress.unitNo'
            fullWidth
            variant='standard'
          />
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: '50%'
        }}
      >
        <FormLabel className='formSmallLabel'>
          Building Name (Optional)
        </FormLabel>
        <TextField
          data-cy='buildingName'
          fullWidth
          sx={{ width: '90%' }}
          id='deliveryAddress.buildingName'
          name='deliveryAddress.buildingName'
          variant='standard'
          onChange={(e) => {
            formik.handleBlur(e)
            formik.handleChange(e)
          }}
          value={formik.values.deliveryAddress.buildingName}
          error={
            formik.touched.deliveryAddress?.buildingName &&
            Boolean(formik.errors.deliveryAddress?.buildingName)
          }
          helperText={
            (formik.values.deliveryAddress.unitNo === '' &&
              formik.errors.deliveryAddress?.buildingName) ||
            (formik.touched.deliveryAddress?.buildingName &&
              formik.errors.deliveryAddress?.buildingName)
          }
        />
      </Stack>
      <Stack
        sx={{
          width: '50%',
          marginTop: '8px'
        }}
      >
        <FormLabel className='formSmallLabel'>Postal Code</FormLabel>
        <TextField
          data-cy='postalCode'
          sx={{ width: '90%' }}
          name='deliveryAddress.postalCode'
          variant='standard'
          size='small'
          onChange={(e) => {
            formik.handleBlur(e)
            formik.handleChange(e)
            handleAddressChange(e)
          }}
          value={formik.values.deliveryAddress.postalCode}
          error={
            formik.values.deliveryAddress.postalCode === '' ||
            (formik.touched.deliveryAddress?.postalCode &&
              Boolean(formik.errors.deliveryAddress?.postalCode))
          }
          helperText={
            (formik.values.deliveryAddress.postalCode === '' &&
              formik.errors.deliveryAddress?.postalCode) ||
            (formik.touched.deliveryAddress?.postalCode &&
              formik.errors.deliveryAddress?.postalCode)
          }
        />
      </Stack>
      <Box>
        <Typography variant='h6' sx={{ color: 'primary.dark' }}>
          Billing Address
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px'
          }}
        >
          <Checkbox
            data-cy='billingAddrSameChk'
            sx={{ color: '#0000FF', marginRight: '10px' }}
            checked={isBillingAddrSame}
            onChange={handleBillingCheckbox}
          />
          Billing address is the same as delivery address
        </Box>
        {!isBillingAddrSame && (
          <>
            <Stack direction='row'>
              <Stack sx={{ width: '50%' }}>
                <FormLabel sx={{ fontSize: '14px', color: 'neutrals.500' }}>
                  Address
                </FormLabel>
                <TextField
                  data-cy='billingAddress'
                  sx={{
                    width: '90%'
                  }}
                  name='billingAddress.streetName'
                  variant='standard'
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  value={formik.values.billingAddress.streetName}
                  error={
                    formik.values.billingAddress.streetName === '' ||
                    (formik.touched.billingAddress?.streetName &&
                      Boolean(formik.errors.billingAddress?.streetName))
                  }
                  helperText={
                    (formik.values.billingAddress.streetName === '' &&
                      formik.errors.billingAddress?.streetName) ||
                    (formik.touched.billingAddress?.streetName &&
                      formik.errors.billingAddress?.streetName)
                  }
                />
              </Stack>
              <Stack sx={{ width: '50%' }}>
                <FormLabel sx={{ fontSize: '14px', color: 'neutrals.500' }}>
                  Unit No.
                </FormLabel>
                <TextField
                  data-cy='billingUnitNo'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>#</InputAdornment>
                    )
                  }}
                  sx={{
                    width: '90%'
                  }}
                  value={formik.values.billingAddress.unitNo}
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  error={
                    formik.values.billingAddress.unitNo === '' ||
                    (formik.touched.billingAddress?.unitNo &&
                      Boolean(formik.errors.billingAddress?.unitNo))
                  }
                  helperText={
                    (formik.values.billingAddress.unitNo === '' &&
                      formik.errors.billingAddress?.unitNo) ||
                    (formik.touched.billingAddress?.unitNo &&
                      formik.errors.billingAddress?.unitNo)
                  }
                  id='billingAddress.unitNo'
                  name='billingAddress.unitNo'
                  variant='standard'
                />
              </Stack>
            </Stack>

            <Box sx={{ marginTop: '12px', width: '50%' }}>
              <FormLabel sx={{ fontSize: '14px', color: 'neutrals.500' }}>
                Building Name (Optional)
              </FormLabel>
              <TextField
                data-cy='billingBuildingName'
                id='billingAddress.buildingName'
                name='billingAddress.buildingName'
                variant='standard'
                sx={{
                  width: '90%'
                }}
                onChange={(e) => {
                  formik.handleBlur(e)
                  formik.handleChange(e)
                }}
                value={formik.values.billingAddress.buildingName}
                error={
                  formik.touched.billingAddress?.buildingName &&
                  Boolean(formik.errors.billingAddress?.buildingName)
                }
                helperText={
                  formik.touched.billingAddress?.buildingName &&
                  formik.errors.billingAddress?.buildingName
                }
              />
            </Box>
            <Stack sx={{ width: '50%', marginTop: '12px' }}>
              <FormLabel sx={{ fontSize: '14px', color: 'neutrals.500' }}>
                Postal Code
              </FormLabel>
              <TextField
                data-cy='billingPostalCode'
                name='billingAddress.postalCode'
                variant='standard'
                size='small'
                onChange={(e) => {
                  formik.handleBlur(e)
                  formik.handleChange(e)
                }}
                sx={{
                  width: '90%'
                }}
                value={formik.values.billingAddress.postalCode}
                error={
                  formik.values.billingAddress.postalCode === '' ||
                  (formik.touched.billingAddress?.postalCode &&
                    Boolean(formik.errors.billingAddress?.postalCode))
                }
                helperText={
                  (formik.values.billingAddress.postalCode === '' &&
                    formik.errors.billingAddress?.postalCode) ||
                  (formik.touched.billingAddress?.postalCode &&
                    formik.errors.billingAddress?.postalCode)
                }
              />
            </Stack>
          </>
        )}
      </Box>
    </div>
  )
}

export default ReviewOrder
